/**
 * `mountScene(canvas, { tier })`: the scene view (integration plan slice 6a,
 * ADR 0031), next to the dungeon's `mount`. It draws a walkable place from
 * its scene record (`@ab/content-schema` `Scene`): a town or an interior with
 * lantern-water's look (the terrain, every placement of the prefab catalog,
 * merged by material (`batch.ts` `mergeStatic`), the canal, the lights, the
 * GPU points, the light decals and shafts, the halos), or, since slice 7a
 * (ADR 0033), the airship's deck and rooms with brad-airship's
 * (`ship/world.ts`: the hull, rig, rooms and their reveal roofs, the sky,
 * wind, flight, lamps, crew and clutter cards); and the characters (sprites
 * from the sprites repo's sheets, or the renderer's proxies) where the
 * walking model (`@ab/scene` `SceneState`) says they stand. Game axes (x
 * east, y north, h up) map to three.js (x, h, −y), here and only here.
 *
 * The shell drives it: `update(dt, model)`, which answers when the view next
 * wants an update (0: the next animation frame; a number of ms: a sprite's
 * idle loop at Off; null: nothing until something changes). At Medium every
 * update draws a frame (the town and the ship animate continuously). At Off a
 * frame is drawn only when something changed (the model's `revision`: a
 * character moved or turned; the camera eased; a sprite's loop stepped, at
 * most `offIdleFps` a second; a time of day blending; a hatch playing; a
 * show, resize, tier change or a landed sheet or texture, which `onChange`
 * announces), as the dungeon idles. The motion strength (`setMotion`: 0 under
 * the system's reduced-motion preference, as the prototype's
 * `motionStrength`) scales the environment's clock and stops the points, the
 * birds, the wind and the motes.
 *
 * The time of day (ADR 0033) is one of the scene record's lighting presets
 * (`setLighting(name)`); a change blends over the record's `lighting.blend`
 * seconds (`look.ts`). A town's day and night are two presets; the airship
 * has five.
 *
 * - **Medium (the stress test, as-is):** the sun's shadow map (cached:
 *   redrawn on a build, a tier change, a moving sun or a part of the ship
 *   appearing or going, never per frame), the town's shadowed spot lights and
 *   every point light, the GPU points (the town's 318, the ship's 180 motes),
 *   the reflection, the leader's cutaway, the town's selective silhouette, the
 *   birds, contact occlusion, bloom, depth focus and the grade (`lens.ts`),
 *   pixel ratio at most 1.5. A sprite casts its projected cut-out; a proxy
 *   (no sheet yet) its blob disc.
 * - **Off:** the sun and the sky only, no shadow map, no points, no birds, no
 *   wind, no sky shader, no post, no reflection, blob shadows, pixel ratio 1;
 *   the same merged geometry and textures. Going Off frees every Medium-only
 *   buffer, target and material program.
 *
 * A portal crossing is a new `show`: the new world is built, compiled and
 * drawn once before the old one is disposed (gotcha
 * "floor-reload-program-lifetime"). A lost WebGL context frees every GPU
 * resource and rebuilds the world from the last record on restore.
 */
import type { Scene, ShipRoomRules, SpriteSource, Theme } from '@ab/content-schema';
import { type SceneState, type WalkMap, createWalkMap, shipLayout } from '@ab/scene';
import * as THREE from 'three';
import { type ProjectedActor, type ProjectedShadows, createProjectedShadows } from '../character-shadows.ts';
import { presentation } from '../constants.ts';
import { townCutawayHook, updateCutaway } from '../cutaway.ts';
import { type GpuTimer, createGpuTimer } from '../gpu-timer.ts';
import { halfFloatTarget } from '../graphics.ts';
import { type Lens, createLens } from '../lens.ts';
import { type ShaderHook, addShaderHook, hookKeys } from '../material-hooks.ts';
import { type Palette, paletteOf } from '../palette.ts';
import { precompile } from '../precompile.ts';
import { shipLensUniforms, shipSlots, townSlots } from '../shaders/lens.ts';
import { type SpriteSheet, loadSheet, preparedSheet } from '../sprites.ts';
import { type SpriteState, UnitLayer, type UnitPose } from '../units.ts';
import type { Projection, QualityTier, SceneStats, ShipStats, ViewStats } from '../view.ts';
import { bakeSceneZones, zoneAt } from '../zones.ts';
import { createTownAtmosphere, type TownAtmosphere } from '../atmosphere.ts';
import { mergeStatic } from '../batch.ts';
import { shipLook } from '../ship/constants.ts';
import { type ShipWorld, buildShipWorld } from '../ship/world.ts';
import { type BirdActor, type Birds, createBirds } from './birds.ts';
import { createSceneCamera, type SceneCamera } from './camera.ts';
import { townPrefab } from './catalog.ts';
import { sceneLook } from './constants.ts';
import { type ArtworkSlot, type Kit, artworkSlots, contextFor, createKit, seedOf } from './kit.ts';
import { type Glows, collectGlows } from './glows.ts';
import { createTravelerHint, type TravelerHint } from './hint.ts';
import { createSceneLights, type SceneLights } from './lights.ts';
import { type Look, type LookBlend, applyLookGrade, createLookBlend, looksOf } from './look.ts';
import { createParticles, type Particles } from './particles.ts';
import { buildTerrain, districtGlsl } from './terrain.ts';
import { type CanalWater, createWater } from './water.ts';

/** A scene's time of day: the name of one of its record's lighting presets (`day`, `night`; the airship's `dawn` … `night`). */
export type SceneLighting = string;

export type SceneShowOptions = {
  /** Where the camera starts (a spawn of the scene; the first when absent). */
  readonly spawn?: string;
  /** The time of day (a preset of the record; the record's `lighting.default` when absent or unknown). */
  readonly lighting?: SceneLighting;
  /** Shipped textures by asset slot (the town's `paving` … `caustics`; the airship's `clutter` and `hatch`): `virtual:ab-assets`. */
  readonly textures?: Readonly<Record<string, string>>;
  /** The traveler's species (`human.brad`): the sprite sheet it draws with. */
  readonly traveler: string;
  /** Proxy colours from a theme (optional; the scene's own otherwise). */
  readonly theme?: Theme;
  /** The walking model of this scene, when the shell has it already: the first frame draws the characters where it has them. */
  readonly model?: SceneState;
  /** A ship scene's room types (`rules/ship_rooms.yaml`) and the scene holding its room grid (ADR 0033). */
  readonly ship?: { readonly rooms: ShipRoomRules; readonly grid: Scene };
};

/** Medium's systems a GPU A/B run may turn off (`setAb`): true is the look. */
export type SceneAbFlags = {
  readonly birds: boolean;
  readonly occlusion: boolean;
  readonly reflection: boolean;
  readonly shimmer: boolean;
  readonly particles: boolean;
  readonly bloom: boolean;
  /** The measurement's method, not the look: true redraws the sun's shadow map every frame (the prototype's `performance.json` timing). */
  readonly shadowEveryFrame: boolean;
};
const abDefaults: SceneAbFlags = { birds: true, occlusion: true, reflection: true, shimmer: true, particles: true, bloom: true, shadowEveryFrame: false };

export type GroundPoint = { readonly x: number; readonly y: number; readonly h: number };

/** A material of the built scene as the tests probe it: its hooks, colour, bump scale and the image its map shows. */
export type MaterialProbe = { readonly hooks: readonly string[]; readonly color: string; readonly bumpScale: number; readonly map: string | null };

/** The contact occlusion probed at a point (tests, Medium): see `probeOcclusion`. */
export type OcclusionProbe = {
  /** The composite over the beauty image there, with the occlusion forced to `ao` (the prototype's `mix(1, ao, 0.72)` on a surface below the highlight threshold). */
  readonly ratio: number;
  /** The beauty image's brightest channel there (linear). */
  readonly beauty: number;
  /** The screen's luminance there (0 to 1) with the occlusion forced to 1 and to `ao`. */
  readonly screen: readonly [number, number];
  /** What the lens pass reads: the composite whenever occlusion is on. */
  readonly lensInput: 'composite' | 'beauty';
};

/** A character the scene shows that the walking model does not hold (a hatchling at its hatch spot: ADR 0033). */
export type SceneGuest = { readonly id: string; readonly species: string; readonly x: number; readonly y: number; readonly facing: UnitPose['facing'] };

/** An incubator's look: no egg, an egg incubating, an egg ready to hatch (ADR 0033). */
export type EggState = 'empty' | 'incubating' | 'ready';

export type SceneView = {
  /** Builds `scene` (disposing the previous one after the new one has drawn). */
  show(scene: Scene, options: SceneShowOptions): void;
  /**
   * One animation frame: the characters where `model` has them, the camera eased by `dt` seconds. Returns when the
   * view next wants an update: 0 the next animation frame, a number of ms, or null (nothing until a change).
   */
  update(dt: number, model: SceneState): number | null;
  /** The motion strength: 1, or 0 under reduced motion (no flicker, breeze, wind or water motion, no points, the birds still). */
  setMotion(strength: number): void;
  /** The time of day now, on the scene shown and every later one that has the preset (ADR 0032, 0033); no rebuild. */
  setLighting(lighting: SceneLighting): void;
  /** The preset names of the scene shown, in the record's order (the pause menu's time row cycles them). */
  lightings(): readonly string[];
  setTier(tier: QualityTier): void;
  readonly tier: QualityTier;
  /** The id of the scene shown (null before the first `show`). */
  readonly sceneId: string | null;
  resize(width: number, height: number): void;
  /** Where a point (game axes) is on screen, CSS pixels of the canvas. */
  project(x: number, y: number, h: number): Projection;
  /** Where a character's head is on screen (`player`, a resident's human id, or a guest's id); null for none. */
  projectActor(id: string): Projection | null;
  /** The ground under a point of the canvas: the camera's ray to the walkable surfaces' height; null off the ground or onto ground nobody can stand on. */
  pickGround(x: number, y: number): GroundPoint | null;
  stats(): ViewStats;
  resetPeak(): void;
  loadSprites(sources: readonly SpriteSource[]): Promise<readonly string[]>;
  spriteState(id: string): SpriteState | null;
  /** The camera jumps to the traveler on the next update instead of easing (the traveler was placed). */
  snapCamera(): void;
  /** Another framing of the scene's (`camera.presets`; the airship's `overview` for captures); unknown names keep the current one. */
  setFraming(name: string): void;
  /** The airship under way (1) or moored (0): sails set, the jetty astern, streaks (ADR 0033). */
  setFlight(flying: boolean): void;
  /** Each incubator's egg by interactable id (the shell's jobs). */
  setEggs(states: Readonly<Record<string, EggState>>): void;
  /**
   * Plays the hatch sequence at a hatch spot on the view's event clock (real time: reduced motion only stills the
   * wobble); resolves when the sequence has run (at once when the scene has no such spot, the view is disposed, the
   * scene changes or the context is lost).
   */
  playHatch(spot: string): Promise<void>;
  /** Characters beside the walking model's (a hatchling where it hatched). */
  setGuests(guests: readonly SceneGuest[]): void;
  /** Which surface textures show shipped artwork. */
  artwork(): Readonly<Partial<Record<ArtworkSlot | 'caustics', boolean>>>;
  /** Tests: the first material of the built scene named `name`, or null. */
  material(name: string): MaterialProbe | null;
  /**
   * Tests (Medium, a built scene): draws the frame with the occlusion forced to 1 and then to `ao`, and reads at a
   * canvas point (CSS px) the composite over the beauty image and the screen's luminance; null at Off.
   */
  probeOcclusion(x: number, y: number, ao: number): OcclusionProbe | null;
  /** Each flock now (tests). */
  flocks(): readonly { readonly id: string; readonly status: string }[];
  /**
   * Tests and GPU A/B runs only (the scene hook, `apps/web/test/gpu-ab.spec.ts`): turns Medium's systems off one by one
   * to measure each one's cost; every flag true (the default) is the look. Never called by the game.
   */
  setAb(flags: Partial<SceneAbFlags>): void;
  dispose(): { readonly geometries: number; readonly textures: number };
};

const syncPixel = new Uint8Array(4);
const tileMetres = presentation.sprite.tileMetres;

/** Proxy colours where no theme is given. */
const scenePalette = (): Palette => {
  const p = sceneLook.proxy;
  const c = (hex: string): THREE.Color => new THREE.Color(hex);
  return {
    background: c(p.background),
    floor: c(p.trim),
    wall: c(p.trim),
    fog: c(p.background),
    party: c(p.party),
    enemy: c(p.resident),
    boss: c(p.resident),
    spring: c(p.head),
    exit: c(p.head),
    trim: c(p.head),
    timber: c(p.trim),
    iron: c(p.trim),
    hit: c(p.head),
    heal: c(p.head),
    cast: c(p.head),
    status: c(p.head),
    rail: c(p.trim),
    sleeper: c(p.head),
    foliage: c(p.trim),
    ore: c(p.trim),
    rope: c(p.trim),
    crystal: c(p.head),
  };
};

/** What a town or interior adds to a world (lantern-water's systems). */
type TownParts = {
  readonly kit: Kit;
  readonly water: CanalWater | null;
  readonly particles: Particles;
  readonly birds: Birds;
  readonly glows: Glows;
  readonly shafts: readonly THREE.ShaderMaterial[];
};

/** One built scene: everything its record made, owned until the next scene has drawn. */
type World = {
  readonly scene: Scene;
  readonly root: THREE.Group;
  readonly town: TownParts | null;
  readonly ship: ShipWorld | null;
  readonly lights: SceneLights;
  readonly atmosphere: TownAtmosphere | null;
  readonly merged: number;
  readonly lens: Lens;
  readonly zoneMap: THREE.DataTexture;
  readonly shadows: ProjectedShadows;
  readonly camera: SceneCamera;
  readonly indoor: boolean;
  /** The light zones in three.js axes (the traveler's mood samples them). */
  readonly zones: readonly { readonly rect?: readonly [number, number, number, number]; readonly circle?: readonly [number, number, number]; readonly feather: number; readonly temp: number; readonly light: number; readonly air: number }[];
  readonly fog: THREE.FogExp2;
  readonly looks: ReadonlyMap<string, Look>;
  readonly blend: LookBlend;
  dispose(): void;
};

/** The canal's footprint as GLSL over `shadowGround` (world x, z): a projected shadow drops where the canal is open. */
const channelDiscard = (scene: Scene): string => {
  const f = (v: number): string => v.toFixed(3);
  const inRect = (r: { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number }): string => `(shadowGround.x>${f(r.x0)}&&shadowGround.x<${f(r.x1)}&&-shadowGround.y>${f(r.y0)}&&-shadowGround.y<${f(r.y1)})`;
  const parts = scene.channels.map((c) => `(${inRect(c)}${c.openings.map((o) => `&&!${inRect(o)}`).join('')})`);
  return parts.length === 0 ? '' : `if(${parts.join('||')})discard;`;
};

/** The ship's texture slots (ADR 0033): atlases the ship world draws its cards with. */
const shipSlots3 = new Set(['clutter', 'hatch']);

const isShip = (scene: Scene): boolean => scene.kind === 'ship_deck' || scene.kind === 'ship_room';

/** `onChange`: called when the view wants a frame by itself (a sheet or texture landed, the context came back). */
export const mountScene = (canvas: HTMLCanvasElement, options: { readonly tier: QualityTier; readonly onChange?: () => void }): SceneView => {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
  renderer.info.autoReset = false;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const three = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 1, 0.5, 150);
  const halfFloat = halfFloatTarget(renderer.getContext());
  let tier: QualityTier = options.tier;
  let world: World | null = null;
  let current: { scene: Scene; options: SceneShowOptions } | null = null;
  let model: SceneState | null = null;
  let gpu: GpuTimer = createGpuTimer(renderer.getContext());
  let disposed = false;
  let lost = false;
  let losses = 0;
  let restores = 0;
  let size = { width: 1, height: 1 };
  let last = { drawCalls: 0, triangles: 0, frames: 0 };
  let peak = { drawCalls: 0, triangles: 0 };
  let buildMs: number | null = null;
  let buildFrame: ViewStats['buildFrame'] = null;
  const cpuSamples: number[] = [];
  const intervals: number[] = [];
  let lastFrameAt: number | null = null;
  let palette: Palette = scenePalette();
  let snapCamera = true;
  let dirty = true;
  let lastIdle = 0;
  let time = 0;
  let motion = 1;
  let revision = -1;
  let drawnRevision = -1;
  let ab: SceneAbFlags = abDefaults;
  /** The time of day asked for (kept across scenes: a scene without that preset shows its default). */
  let lightingAsked: SceneLighting | null = null;
  let flight = 0;
  let framing: string | null = null;
  let eggs: Readonly<Record<string, EggState>> = {};
  let guests: readonly SceneGuest[] = [];
  /**
   * The event clock (seconds, real time: never scaled by the motion strength), on which an event's sequence runs (the
   * hatch), so reduced motion cannot freeze it; and the sequence playing, if any, with what ends it.
   */
  let eventTime = 0;
  let hatchPlaying: { readonly started: number; readonly seconds: number; readonly done: () => void } | null = null;
  const endHatch = (): void => {
    const h = hatchPlaying;
    hatchPlaying = null;
    h?.done();
  };
  /** The lens's sun glare at the last Medium frame of a ship scene (stats). */
  let lastGlare = 0;
  const wake = (): void => {
    options.onChange?.();
  };
  const mood = new THREE.Vector3();
  const here = new THREE.Vector3();
  const images = new Map<string, Promise<HTMLImageElement>>();
  /**
   * The sprites' light at Medium: a self-light so the traveler stays legible (`main.js`: emissive 0.8 in a town). On
   * the airship Brad is lit as the prototype lit him (`airship-main.js`): emissive 0.32, plus the ship's baked lamp
   * light on deck and the rooms' radiance volume below deck, read from the ship world's volumes each frame (one
   * program for every scene: the ship's terms are off where their uniforms say so).
   */
  const spriteShip = {
    abLampVolume: { value: null as THREE.Data3DTexture | null },
    abLampMin: { value: new THREE.Vector3() },
    abLampSpan: { value: new THREE.Vector3(1, 1, 1) },
    abLampLevel: { value: 0 },
    abRadiance: { value: null as THREE.Data3DTexture | null },
    abRadianceMin: { value: new THREE.Vector3() },
    abRadianceSpan: { value: new THREE.Vector3(1, 1, 1) },
    abRadianceOn: { value: 0 },
  };
  const selfLightValue: THREE.IUniform<number> = { value: sceneLook.emissive.traveler };
  const selfLight: ShaderHook = {
    key: 'scene-sprite-self-light',
    uniforms: { abSelfLight: selfLightValue, ...spriteShip },
    vertexHeader: 'varying vec3 abSpriteWorld;',
    fragmentHeader: `uniform float abSelfLight;uniform highp sampler3D abLampVolume,abRadiance;uniform vec3 abLampMin,abLampSpan,abRadianceMin,abRadianceSpan;
uniform float abLampLevel,abRadianceOn;varying vec3 abSpriteWorld;`,
    vertex: [
      {
        chunk: 'worldpos_vertex',
        after: `vec4 abSpriteVertex=vec4(transformed,1.);
#ifdef USE_INSTANCING
  abSpriteVertex=instanceMatrix*abSpriteVertex;
#endif
abSpriteWorld=(modelMatrix*abSpriteVertex).xyz;`,
      },
    ],
    fragment: [
      { chunk: 'emissivemap_fragment', after: 'totalEmissiveRadiance+=diffuseColor.rgb*abSelfLight;' },
      {
        chunk: 'lights_fragment_end',
        after: `if(abRadianceOn>.5){
  vec3 abUvw=(abSpriteWorld-abRadianceMin)/abRadianceSpan;
  if(all(greaterThanEqual(abUvw,vec3(0.)))&&all(lessThanEqual(abUvw,vec3(1.)))){
    vec4 abBaked=texture(abRadiance,abUvw);
    reflectedLight.directDiffuse*=abBaked.a;reflectedLight.directSpecular*=abBaked.a;
    reflectedLight.indirectDiffuse*=mix(1.,.23,1.-abBaked.a);
    reflectedLight.indirectDiffuse+=diffuseColor.rgb*abBaked.rgb*3.;
  }
}
if(abLampLevel>.001){
  vec3 abLampUvw=(abSpriteWorld-abLampMin)/abLampSpan;
  if(all(greaterThanEqual(abLampUvw,vec3(0.)))&&all(lessThanEqual(abLampUvw,vec3(1.))))
    reflectedLight.indirectDiffuse+=diffuseColor.rgb*texture(abLampVolume,abLampUvw).rgb*2.1*abLampLevel;
}`,
      },
    ],
  };
  const proxyMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 });
  const box = new THREE.BoxGeometry(1, 1, 1);
  const disc = new THREE.CircleGeometry(1, presentation.unit.shadow.segments).rotateX(-Math.PI / 2);
  const blobMaterial = new THREE.MeshBasicMaterial({ color: sceneLook.blob.color, transparent: true, opacity: sceneLook.blob.opacity, depthWrite: false });
  const units = new UnitLayer(three, box, disc, proxyMaterial, blobMaterial, selfLight);
  /** Each walker's distance at the last frame, and its place in the stride cycle (`main.js` `heroCycle`). */
  const strides = new Map<string, { distance: number; cycle: number }>();
  units.setSpriteCasting(false);
  const hint: TravelerHint = createTravelerHint();
  const requested = new Set<string>();
  const installed = new Set<string>();
  /** Where each character's feet are now (game axes), by id. */
  const feet = new Map<string, GroundPoint>();
  const travelerId = 'player';
  const atlases = new Map<string, THREE.Texture>();

  const medium = (): boolean => tier === 'medium';

  /** The look to show for a scene: the one asked for if it has it, else its default. */
  const presetFor = (scene: Scene): string => (lightingAsked !== null && Object.hasOwn(scene.lighting.presets, lightingAsked) ? lightingAsked : scene.lighting.default);

  const lensOf = (scene: Scene, ship: boolean, terrain: Scene['terrain']): Lens => {
    if (ship) {
      const lens = createLens(halfFloat, { slots: shipSlots(), occlusion: shipLook.occlusion, bloom: shipLook.bloom, uniforms: shipLensUniforms() });
      lens.uniforms.waterMask.value = 0;
      lens.uniforms.shadowLift.value.set(...shipLook.lens.shadowLift);
      lens.uniforms.shadowChroma.value = shipLook.lens.shadowChroma;
      return lens;
    }
    const haze = scene.grade.hazeRect ?? scene.bounds;
    return createLens(halfFloat, {
      slots: townSlots({
        taps: sceneLook.lens.taps,
        tapPixels: sceneLook.lens.tapPixels,
        focus: [sceneLook.lens.focusNear, sceneLook.lens.focusFar],
        ground: terrain === undefined ? '0.' : districtGlsl(terrain, 'world.xz'),
        haze: [(haze.x0 + haze.x1) / 2, -(haze.y0 + haze.y1) / 2, (haze.x1 - haze.x0) / 2, (haze.y1 - haze.y0) / 2],
      }),
      occlusion: sceneLook.occlusion,
      bloom: sceneLook.bloom,
    });
  };

  const buildWorld = (scene: Scene, opts: SceneShowOptions): World => {
    const ship = isShip(scene);
    const terrain = scene.terrain;
    const water = scene.water;
    const root = new THREE.Group();
    root.name = `scene:${scene.id}`;
    const map: WalkMap = model?.scene.id === scene.id ? model.map : createWalkMap(scene);
    const groundAt = (x: number, z: number): number => map.heightAt(x, -z);
    let town: TownParts | null = null;
    let shipWorld: ShipWorld | null = null;
    let merged = 0;
    if (ship) {
      const grid = opts.ship?.grid ?? scene;
      const on = scene.onShip;
      const rooms = opts.ship === undefined || on === undefined ? [] : shipLayout(grid, opts.ship.rooms).filter((r) => r.room.deck === on.deck);
      shipWorld = buildShipWorld({ scene, rooms, map });
      root.add(shipWorld.root);
      merged = shipWorld.merged;
    } else {
      const kit = createKit({ coast: { west: terrain?.river.west ?? sceneLook.coast.west, east: terrain?.river.east ?? sceneLook.coast.east, waterY: water?.level ?? sceneLook.coast.waterY } });
      if (terrain !== undefined) root.add(buildTerrain(contextFor(kit, seedOf(`${scene.id}:terrain`)), terrain, water?.bridges.map((b) => b.y) ?? []));
      for (const p of scene.placements) {
        const prefab = townPrefab(p.prefab);
        if (prefab === null) throw new Error(`${scene.id}: ${p.prefab} is a ship prefab`);
        const built = prefab.buildPrefab(contextFor(kit, seedOf(p.id), p.params ?? {}), p.params ?? {});
        built.root.name = `prefab:${p.id}`;
        built.root.position.set(p.x, p.h ?? 0, -p.y);
        built.root.rotation.y = THREE.MathUtils.degToRad(p.rotation ?? 0);
        root.add(built.root);
      }
      const glows = collectGlows(root, kit.glowMap);
      const shafts: THREE.ShaderMaterial[] = [];
      root.traverse((o) => {
        if (o instanceof THREE.Mesh && o.name === 'shop-window-shaft' && o.material instanceof THREE.ShaderMaterial) shafts.push(o.material);
      });
      const cutawayDone = new Set<THREE.Material>();
      merged = mergeStatic(root, kit.contact, (material) => {
        if (cutawayDone.has(material)) return;
        cutawayDone.add(material);
        addShaderHook(material, townCutawayHook);
      }).merged;
      if (glows.mesh !== null) root.add(glows.mesh);
      const canal = water !== undefined && terrain !== undefined ? createWater(kit, water, terrain, halfFloat) : null;
      if (canal !== null) root.add(canal.mesh);
      const particles = createParticles(scene.emitters, scene.kind === 'interior');
      root.add(particles.group);
      const birds = createBirds(scene.birds, groundAt);
      root.add(birds.mesh);
      town = { kit, water: canal, particles, birds, glows, shafts };
    }
    const lights = createSceneLights(scene, ship ? shipLook.sun : undefined);
    root.add(lights.group);
    const atmosphere =
      scene.atmosphere === undefined
        ? null
        : createTownAtmosphere(
            {
              shafts: scene.atmosphere.shafts.map((s) => ({ at: [s.x, -s.y] as const, width: s.width, length: s.length })),
              shade: scene.atmosphere.shade.map((s) => ({ x: s.x, z: -s.y, hx: s.hx, hz: s.hy, strength: s.strength })),
              trees: scene.placements.filter((p) => p.prefab === 'street_tree').map((p) => ({ x: p.x, z: -p.y, size: typeof p.params?.['size'] === 'number' ? p.params['size'] : 1 })),
              lamps: (water?.lamps ?? []).map((l) => ({ x: l.x, z: -l.y })),
              waterBand: sceneLook.lens.waterBand,
              ...(ship ? { shaftColor: sceneLook.shipAir.shaftColor, poolTint: sceneLook.shipAir.poolTint } : {}),
            },
            groundAt,
          );
    if (atmosphere !== null) root.add(atmosphere.shafts, atmosphere.decals);
    const g = scene.grade;
    const zoneRect = [g.zoneRect.x0, -g.zoneRect.y1, g.zoneRect.x1 - g.zoneRect.x0, g.zoneRect.y1 - g.zoneRect.y0] as const;
    const zones = g.zones.map((z) => ({
      ...(z.rect === undefined ? {} : { rect: [z.rect.x0, -z.rect.y1, z.rect.x1, -z.rect.y0] as const }),
      ...(z.circle === undefined ? {} : { circle: [z.circle.x, -z.circle.y, z.circle.r] as const }),
      feather: z.feather,
      temp: z.temp,
      light: z.light,
      air: z.air ?? 0,
    }));
    const zoneMap = bakeSceneZones(zones, zoneRect, g.zoneTexelsPerMetre, (water?.lamps ?? []).map((l) => ({ x: l.x, z: -l.y })));
    const lens = lensOf(scene, ship, terrain);
    const u = lens.uniforms;
    u.zoneMap.value = zoneMap;
    u.zoneRect.value.set(...zoneRect);
    u.zoneGround.value = 0;
    if (!ship) {
      u.waterMask.value = water === undefined ? 0 : 1;
      u.waterBand.value.set(...sceneLook.lens.waterBand);
      u.shadowLift.value.set(...sceneLook.lens.shadowLift);
      u.shadowChroma.value = sceneLook.lens.shadowChroma;
      u.hazeRange.value.set(...sceneLook.lens.hazeRange);
      u.strength.value = sceneLook.lens.strength;
    }
    const shadows = createProjectedShadows(three, channelDiscard(scene), sceneLook.characterShadow);
    three.add(root);
    const sceneCamera = createSceneCamera(scene);
    sceneCamera.setBounds(shipWorld?.bounds ?? null);
    if (framing !== null) sceneCamera.setFraming(framing);
    const looks = looksOf(scene);
    const first = looks.get(presetFor(scene)) ?? looks.get(scene.lighting.default);
    if (first === undefined) throw new Error(`${scene.id}: lighting.default ${scene.lighting.default} is not a preset`);
    const fog = new THREE.FogExp2(first.background.getHex(), first.fogDensity);
    return {
      scene,
      root,
      town,
      ship: shipWorld,
      lights,
      atmosphere,
      merged,
      lens,
      zoneMap,
      shadows,
      camera: sceneCamera,
      indoor: scene.kind === 'interior' || scene.kind === 'ship_room',
      zones,
      fog,
      looks,
      blend: createLookBlend(first),
      dispose() {
        root.removeFromParent();
        if (town !== null) {
          root.traverse((o) => {
            if (o instanceof THREE.Mesh && o.userData['dynamic'] !== true) (o.geometry as THREE.BufferGeometry).dispose();
          });
          town.water?.dispose();
          town.particles.dispose();
          town.birds.dispose();
          town.glows.dispose();
          for (const m of town.shafts) m.dispose();
          town.kit.dispose();
        }
        shipWorld?.dispose();
        lights.dispose();
        atmosphere?.dispose();
        lens.dispose();
        zoneMap.dispose();
        shadows.dispose();
      },
    };
  };

  const loadImage = (url: string): Promise<HTMLImageElement> => {
    let image = images.get(url);
    if (image === undefined) {
      image = new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve(img);
        };
        img.onerror = () => {
          reject(new Error(`texture ${url} did not load`));
        };
        img.src = url;
      });
      images.set(url, image);
    }
    return image;
  };

  /** Loads the shipped textures into a world (cached per URL; a rebuild applies them at once): a town kit's surfaces, a ship's card atlases. */
  const applyArtwork = (w: World, textures: Readonly<Record<string, string>> | undefined): void => {
    if (textures === undefined) return;
    for (const [slot, url] of Object.entries(textures)) {
      const forShip = w.ship !== null && shipSlots3.has(slot);
      if (!forShip && (w.town === null || (slot !== 'caustics' && !Object.hasOwn(artworkSlots, slot)))) continue;
      loadImage(url).then(
        (img) => {
          if (world !== w || disposed) return;
          if (forShip) {
            let t = atlases.get(url);
            if (t === undefined) {
              t = new THREE.Texture(img);
              t.colorSpace = THREE.SRGBColorSpace;
              t.magFilter = THREE.NearestFilter;
              t.minFilter = THREE.NearestFilter;
              t.generateMipmaps = false;
              t.needsUpdate = true;
              atlases.set(url, t);
            }
            w.ship?.setAtlas(slot, t);
          } else w.town?.kit.applyArtwork(slot as ArtworkSlot | 'caustics', img);
          dirty = true;
          if (tier === 'medium') renderer.shadowMap.needsUpdate = true;
          wake();
        },
        (e: unknown) => {
          console.warn(`scene texture ${slot} not applied (the procedural surface stays): ${e instanceof Error ? e.message : String(e)}`);
        },
      );
    }
  };

  const applySize = (): void => {
    renderer.setPixelRatio(medium() ? Math.min(window.devicePixelRatio, sceneLook.pixelRatio.medium) : sceneLook.pixelRatio.off);
    renderer.setSize(size.width, size.height, false);
    world?.camera.frame(camera, size.width / Math.max(1, size.height));
    if (world !== null && medium()) {
      const buffer = renderer.getDrawingBufferSize(new THREE.Vector2());
      world.lens.setSize(buffer.x, buffer.y);
    }
  };

  const applyTier = (): void => {
    const m = medium();
    renderer.shadowMap.enabled = m;
    renderer.shadowMap.needsUpdate = m;
    units.setTier(proxyMaterial, true, m);
    if (world !== null) {
      const t = world.town;
      if (t !== null) {
        t.water?.setTier(m);
        t.particles.setActive(m && ab.particles);
        t.birds.setActive(m && ab.birds);
        t.kit.uniforms.shimmer.value = m && ab.shimmer ? 1 : 0;
        t.kit.uniforms.breezeAmount.value = m ? 1 : sceneLook.breeze.off;
      }
      world.lights.setTier(m);
      world.lens.setOcclusion(m && ab.occlusion);
      world.ship?.setTier(m);
      if (!m) {
        world.lens.release();
        t?.birds.release();
        t?.water?.release();
        world.lights.release();
        world.atmosphere?.release();
        world.zoneMap.dispose();
        hint.release();
        world.shadows.release();
      }
    }
    applySize();
    if (world !== null) {
      if (m) precompile(renderer, three, camera, world.lens.target, world.lens.materials);
      else renderer.compile(three, camera);
    }
    dirty = true;
  };

  const placeActors = (m: SceneState): void => {
    feet.clear();
    feet.set(travelerId, { x: m.player.x, y: m.player.y, h: m.player.h });
    for (const r of m.residents) feet.set(r.human, { x: r.x, y: r.y, h: r.h });
    const sync = (id: string, walker: SceneState['player'], species: string, side: 'party' | 'enemy'): void => {
      const pose = units.get(id) ?? units.addPose({ id, species, side, x: walker.x, y: walker.y, facing: walker.facing });
      pose.x = walker.x;
      pose.y = walker.y;
      pose.facing = walker.facing;
      pose.walking = walker.moving;
      pose.running = walker.running;
      // The stride follows the distance walked: a cycle every `stride.walk` metres walking, `stride.run` running.
      const stride = strides.get(id);
      if (stride === undefined || walker.distance < stride.distance) strides.set(id, { distance: walker.distance, cycle: 0 });
      else {
        const moved = walker.distance - stride.distance;
        stride.cycle = (stride.cycle + moved / (walker.running ? sceneLook.stride.run : sceneLook.stride.walk)) % 1;
        stride.distance = walker.distance;
      }
      pose.cycle = strides.get(id)?.cycle ?? 0;
    };
    sync(travelerId, m.player, current?.options.traveler ?? '', 'party');
    // The residents on the scene now (the airship's crew come and go with the time of day: off deck, none is drawn).
    for (const r of m.residents) {
      if (r.present) sync(r.human, r, r.human, 'enemy');
      else if (units.get(r.human) !== undefined) {
        units.remove(r.human);
        feet.delete(r.human);
      }
    }
    for (const g of guests) {
      feet.set(g.id, { x: g.x, y: g.y, h: m.map.heightAt(g.x, g.y) });
      const pose = units.get(g.id) ?? units.addPose({ id: g.id, species: g.species, side: 'party', x: g.x, y: g.y, facing: g.facing });
      pose.x = g.x;
      pose.y = g.y;
      pose.facing = g.facing;
    }
  };

  const elevation = (p: UnitPose): number => feet.get(p.id)?.h ?? 0;
  const everyone = (): boolean => true;

  const cutoutOf = (id: string): ProjectedActor | null => {
    const at = feet.get(id);
    const cutout = units.cutout(id);
    return at === undefined || cutout === null ? null : { id, x: at.x, y: at.h, z: -at.y, cutout };
  };

  const lightDirection = new THREE.Vector3();
  const settled = new THREE.Vector3();
  const background = new THREE.Color();
  const player3 = new THREE.Vector3();
  let easing = false;
  let shipAnimating = false;
  let blending = false;
  const head = new THREE.Vector3();
  const cutAt = new THREE.Vector3();
  const drawing = new THREE.Vector2();
  const render = (dt: number): void => {
    if (disposed || lost || world === null || model === null) return;
    const w = world;
    const started = performance.now();
    const m = medium();
    const p = model.player;
    settled.copy(w.camera.target);
    w.camera.follow(camera, p.x, p.y, p.h, dt, canvas.clientWidth, snapCamera);
    easing = !snapCamera && settled.distanceTo(w.camera.target) > sceneLook.settle;
    snapCamera = false;
    const now = started;
    units.update(palette, everyone, elevation, camera, now);
    // The time of day: a blend toward the preset asked for, then everything that reads it.
    blending = w.blend.step(dt);
    if (blending && m) renderer.shadowMap.needsUpdate = true;
    const look = w.blend.current;
    const night = look.lamps;
    let sunScale = 1;
    shipAnimating = false;
    if (w.ship !== null) {
      player3.set(p.x, p.h, -p.y);
      const r = w.ship.frame({ dt, time, eventTime, look, medium: m, camera, viewportHeight: renderer.getDrawingBufferSize(drawing).y, player: player3, flight, motion, lens: m ? w.lens : null });
      if (r.shadows && m) renderer.shadowMap.needsUpdate = true;
      sunScale = r.sunScale;
      shipAnimating = r.animating;
      // Brad's light aboard: the ship's lamp bake on deck, the rooms' radiance below (the world's volumes).
      const k = w.ship.kit;
      spriteShip.abLampVolume.value = k.lamp.lampVolume.value;
      spriteShip.abLampMin.value.copy(k.lamp.lampMin.value);
      spriteShip.abLampSpan.value.copy(k.lamp.lampSpan.value);
      spriteShip.abLampLevel.value = k.lamp.lampVolume.value === null ? 0 : k.lamp.lampLevel.value;
      spriteShip.abRadiance.value = k.radiance?.hdRadiance.value ?? null;
      spriteShip.abRadianceOn.value = k.radiance?.hdRadiance.value === null || k.radiance === null ? 0 : 1;
      if (k.radiance !== null) {
        spriteShip.abRadianceMin.value.copy(k.radiance.hdRadianceMin.value);
        spriteShip.abRadianceSpan.value.copy(k.radiance.hdRadianceSpan.value);
      }
    }
    if (ab.shadowEveryFrame && m) renderer.shadowMap.needsUpdate = true;
    w.lights.update(look, time, motion, sunScale);
    renderer.toneMappingExposure = look.exposure;
    w.fog.color.copy(look.background);
    w.fog.density = look.fogDensity;
    background.copy(look.background);
    three.background = background;
    three.fog = w.fog;
    const t = w.town;
    if (t !== null) {
      const k = t.kit;
      k.m.glass.emissiveIntensity = look.glass;
      k.m.lanternGlass.color.set(sceneLook.materials.colors.lanternGlass).multiplyScalar(THREE.MathUtils.lerp(sceneLook.lanternGlass[0], sceneLook.lanternGlass[1], night));
      k.m.windowGlass.emissiveIntensity = look.windows;
      k.m.windowCool.emissiveIntensity = look.coolWindows;
      k.uniforms.breezeTime.value = time;
      k.uniforms.waterTime.value = time;
      k.uniforms.waterNight.value = night;
      for (const s of t.shafts) {
        const strength = s.uniforms['strength'];
        if (strength !== undefined) strength.value = m ? 1 - night : 0;
      }
      t.particles.update(time, night, renderer.getDrawingBufferSize(drawing).y, motion > 0);
      if (m && ab.birds) {
        // The flocks scatter from the traveler and the walking residents (a resident only right beside them).
        const actors: BirdActor[] = [{ x: p.x, z: -p.y }];
        for (const r of model.residents) if (r.patrol !== null) actors.push({ x: r.x, z: -r.y, radius: sceneLook.birds.residentRadius });
        t.birds.update(dt, actors, { enabled: motion > 0, night });
      }
    }
    // The airship's atmosphere follows its night alone (the prototype's evening); the town's its lamps.
    const atmosphereNight = w.ship !== null ? (night >= 1 ? 1 : 0) : night;
    // The characters' sun shadows (by day, outdoors) and the leader's cutaway.
    lightDirection.subVectors(w.lights.sun.target.position, w.lights.sun.position).normalize();
    // A sprite casts its cut-out; a proxy (no sheet yet) keeps its blob disc, never a projected box.
    const actors = [...feet.keys()].map(cutoutOf).filter((a): a is ProjectedActor => a !== null && a.cutout.texture !== null);
    w.shadows.update(m ? actors : [], camera.quaternion, lightDirection, (x, z) => model?.map.heightAt(x, -z) ?? 0, w.indoor ? 0 : sceneLook.characterShadow.opacity * (1 - night));
    const leader = cutoutOf(travelerId);
    // The leader's cutaway: a town's outdoors at Medium; the airship's everywhere and at both tiers (its rooms' ladders
    // and the bridge's posts stand before the traveler; the prototype's `cutaway.update(true)`), at the plane's middle.
    const shipCut = w.ship !== null ? cutAt.set(p.x, p.h + (leader?.cutout.height ?? shipLook.lens.focusHeight * 2) / 2, -p.y) : null;
    renderer.info.reset();
    gpu.begin();
    if (m) {
      const u = w.lens.uniforms;
      // The ship's frame set its glare (the sun on screen); the grade keeps it.
      applyLookGrade(u, look.grade, w.ship === null ? 0 : u.glare.value);
      if (w.ship !== null) lastGlare = u.glare.value;
      if (w.ship === null) {
        u.glare.value = look.grade.glare;
        if (w.indoor) here.set(0, 0, 0);
        else zoneAt(p.x, -p.y, here, w.zones).multiplyScalar(0.5 * u.zoneStrength.value);
        mood.lerp(here, 1 - Math.exp(-dt * sceneLook.lens.zoneMoodRate));
        u.zoneMood.value.copy(mood);
      } else {
        // The ship's strength, glare and far layer were set by its frame; it has no light zones.
        u.zoneStrength.value = 0;
        u.hazeAmount.value = 0;
        u.zoneMood.value.set(0, 0, 0);
      }
      u.night.value = w.ship !== null ? atmosphereNight : night;
      u.time.value = time;
      const focusHeight = w.ship !== null ? shipLook.lens.focusHeight : sceneLook.lens.focusHeight;
      const fit = w.camera.framing === 'overview' && w.ship !== null;
      u.focusDistance.value = fit ? camera.position.distanceTo(w.camera.target) : -head.set(p.x, p.h + focusHeight, -p.y).applyMatrix4(camera.matrixWorldInverse).z;
      w.lens.setBloom(ab.bloom ? look.bloom : 0);
      w.atmosphere?.update(time, atmosphereNight, true, w.lens.depth, camera);
      updateCutaway(camera, null);
      if (ab.reflection) t?.water?.renderReflection(renderer, three, camera);
      updateCutaway(camera, shipCut ?? (w.indoor || leader === null ? null : head.set(leader.x, leader.y + leader.cutout.height / 2, leader.z)));
      if (w.ship === null) {
        hint.set(leader, camera.quaternion, w.lens.depth, camera);
        w.lens.render(renderer, three, camera, (r, target) => {
          hint.draw(r, target, camera);
        });
      } else w.lens.render(renderer, three, camera);
    } else {
      w.atmosphere?.update(time, atmosphereNight, false, null, camera);
      updateCutaway(camera, shipCut);
      renderer.setRenderTarget(null);
      renderer.render(three, camera);
    }
    gpu.end();
    const end = performance.now();
    cpuSamples.push(end - started);
    if (cpuSamples.length > presentation.stats.frames) cpuSamples.shift();
    if (lastFrameAt !== null && end - lastFrameAt < presentation.anim.maxFrameMs * 2) {
      intervals.push(end - lastFrameAt);
      if (intervals.length > presentation.stats.frames) intervals.shift();
    }
    lastFrameAt = end;
    last = { drawCalls: renderer.info.render.calls, triangles: renderer.info.render.triangles, frames: last.frames + 1 };
    drawnRevision = model.revision;
    peak = { drawCalls: Math.max(peak.drawCalls, last.drawCalls), triangles: Math.max(peak.triangles, last.triangles) };
    dirty = false;
  };

  /** Applies the shell's state that outlives a world (eggs) to a fresh one. */
  const applyState = (w: World): void => {
    if (w.ship === null) return;
    for (const [id, state] of Object.entries(eggs)) w.ship.setEgg(id, state);
  };

  const show = (scene: Scene, opts: SceneShowOptions): void => {
    if (disposed) throw new Error('the scene view is disposed');
    current = { scene, options: opts };
    endHatch();
    if (opts.lighting !== undefined) lightingAsked = opts.lighting;
    palette = opts.theme === undefined ? scenePalette() : paletteOf(opts.theme);
    units.clear();
    feet.clear();
    strides.clear();
    guests = [];
    revision = -1;
    units.setScale((scene.spriteScale * sceneLook.mCell.pxPerMetre * tileMetres) / sceneLook.mCell.height);
    // The characters' light and contact shadow: the airship's aboard, the town's elsewhere (its ship terms off).
    const aboard = isShip(scene);
    selfLightValue.value = aboard ? shipLook.sprite.selfLight : sceneLook.emissive.traveler;
    blobMaterial.color.set(aboard ? shipLook.sprite.contact.color : sceneLook.blob.color);
    blobMaterial.opacity = aboard ? shipLook.sprite.contact.opacity : sceneLook.blob.opacity;
    if (!aboard) {
      spriteShip.abLampVolume.value = null;
      spriteShip.abLampLevel.value = 0;
      spriteShip.abRadiance.value = null;
      spriteShip.abRadianceOn.value = 0;
    }
    snapCamera = true;
    if (lost) return;
    const started = performance.now();
    const old = world;
    if (opts.model !== undefined) model = opts.model;
    const next = buildWorld(scene, opts);
    world = next;
    old?.root.removeFromParent();
    applyArtwork(next, opts.textures);
    applyState(next);
    applyTier();
    // A first frame from the spawn (or the model, once the shell steps it).
    if (model === null || model.scene.id !== scene.id) {
      const spawn = scene.spawns.find((s) => s.id === opts.spawn) ?? scene.spawns[0];
      if (spawn !== undefined) feet.set(travelerId, { x: spawn.x, y: spawn.y, h: 0 });
    }
    if (model !== null && model.scene.id === scene.id) {
      placeActors(model);
      render(0);
    } else if (model !== null) {
      // The shell swaps the model with the scene; draw once it does.
      dirty = true;
    }
    if (model === null || model.scene.id !== scene.id) feet.clear();
    old?.dispose();
    buildFrame = { drawCalls: last.drawCalls, triangles: last.triangles };
    peak = { ...peak };
    const gl = renderer.getContext();
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, syncPixel);
    buildMs = performance.now() - started;
  };

  const freeAtlases = (): void => {
    for (const t of atlases.values()) t.dispose();
    atlases.clear();
  };

  const onLost = (e: globalThis.Event): void => {
    e.preventDefault();
    if (lost || disposed) return;
    lost = true;
    losses += 1;
    endHatch();
    world?.dispose();
    world = null;
    freeAtlases();
    units.dispose();
    gpu.dispose();
  };
  const onRestored = (): void => {
    if (!lost || disposed) return;
    lost = false;
    restores += 1;
    gpu = createGpuTimer(renderer.getContext(), gpu);
    units.rebind(box, disc, blobMaterial, selfLight);
    if (current !== null) {
      const { scene, options: opts } = current;
      // Rebuild with the poses kept: the model is still the shell's.
      world = buildWorld(scene, opts);
      applyArtwork(world, opts.textures);
      applyState(world);
      snapCamera = true;
      applyTier();
      if (model !== null) placeActors(model);
      render(0);
    }
    wake();
  };
  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);

  /** Sheets that landed and wait for the next frame (one that lands after `dispose` is dropped: the units are gone). */
  const land = (sheet: SpriteSheet): void => {
    if (disposed) return;
    units.install(sheet);
    installed.add(sheet.id);
    dirty = true;
    wake();
  };

  const ray = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const projectPoint = (x: number, y: number, z: number): Projection => {
    const v = new THREE.Vector3(x, y, z).project(camera);
    const inside = v.z < 1 && Math.abs(v.x) <= 1 && Math.abs(v.y) <= 1;
    return { x: ((v.x + 1) / 2) * canvas.clientWidth, y: ((1 - v.y) / 2) * canvas.clientHeight, visible: inside };
  };

  applyTier();

  return {
    show,
    update(dt, next) {
      if (disposed) return null;
      time += dt * motion;
      eventTime += dt;
      // An event's sequence ends on its own clock, drawn or not (a lost context ends it at once: nothing shows it).
      if (hatchPlaying !== null && (lost || eventTime - hatchPlaying.started >= hatchPlaying.seconds)) endHatch();
      // A new model, or the same one stepped into a new revision (a facing-only turn included), draws.
      const changed = model !== next || revision !== next.revision;
      model = next;
      revision = next.revision;
      if (lost || world === null || next.scene.id !== world.scene.id) return null;
      placeActors(next);
      const moving = next.player.moving || next.residents.some((r) => r.moving);
      if (medium()) {
        render(dt);
        return 0;
      }
      // Off: draw on change only (and a sprite loop's steps, capped; a time of day blending; a hatch playing).
      const now = performance.now();
      const animating = units.animating(everyone);
      const loop = animating && now - lastIdle >= 1000 / sceneLook.offIdleFps;
      const sequence = hatchPlaying !== null;
      if (dirty || changed || moving || snapCamera || easing || loop || blending || shipAnimating || sequence) {
        if (loop) lastIdle = now;
        render(dt);
      }
      if (dirty || snapCamera || easing || blending || shipAnimating || sequence) return 0;
      return animating ? Math.max(1, lastIdle + 1000 / sceneLook.offIdleFps - performance.now()) : null;
    },
    setMotion(strength) {
      if (strength === motion) return;
      motion = strength;
      dirty = true;
      wake();
    },
    setLighting(lighting) {
      lightingAsked = lighting;
      // Kept with the shown scene, so a lost context rebuilds it at this time of day.
      if (current !== null) current = { ...current, options: { ...current.options, lighting } };
      if (world === null) return;
      const look = world.looks.get(lighting);
      if (look === undefined || world.blend.target() === lighting) return;
      world.blend.set(look, world.scene.lighting.blend);
      if (medium()) renderer.shadowMap.needsUpdate = true;
      dirty = true;
      wake();
    },
    lightings: () => (current === null ? [] : Object.keys(current.scene.lighting.presets)),
    setTier(next) {
      if (disposed || next === tier) return;
      tier = next;
      if (lost) return;
      applyTier();
      render(0);
    },
    get tier() {
      return tier;
    },
    get sceneId() {
      return world?.scene.id ?? current?.scene.id ?? null;
    },
    resize(width, height) {
      size = { width, height };
      if (lost) return;
      applySize();
      dirty = true;
      render(0);
      wake();
    },
    project(x, y, h) {
      return projectPoint(x, h, -y);
    },
    projectActor(id) {
      const at = feet.get(id);
      const pose = units.get(id);
      if (at === undefined || pose === undefined) return null;
      return projectPoint(at.x, at.h + units.topOf(pose), -at.y);
    },
    pickGround(x, y) {
      const map = model?.map ?? null;
      if (world === null || map === null || canvas.clientWidth === 0 || canvas.clientHeight === 0) return null;
      pointer.set((x / canvas.clientWidth) * 2 - 1, 1 - (y / canvas.clientHeight) * 2);
      ray.setFromCamera(pointer, camera);
      const o = ray.ray.origin;
      const d = ray.ray.direction;
      const ground = (t: number): number => o.y + d.y * t - map.heightAt(o.x + d.x * t, -(o.z + d.z * t));
      // March the ray to its first crossing of the walkable height field, then bisect.
      const step = sceneLook.pick.step;
      let prev = 0;
      for (let t = camera.near; t < camera.far; t += step) {
        if (ground(t) <= 0) {
          let lo = prev;
          let hi = t;
          for (let i = 0; i < sceneLook.pick.bisect; i += 1) {
            const mid = (lo + hi) / 2;
            if (ground(mid) > 0) lo = mid;
            else hi = mid;
          }
          const px = o.x + d.x * hi;
          const gy = -(o.z + d.z * hi);
          if (!map.canWalk(px, gy)) return null;
          return { x: px, y: gy, h: map.heightAt(px, gy) };
        }
        prev = t;
      }
      return null;
    },
    stats(): ViewStats {
      const counts = world?.town?.particles.counts() ?? { dust: 0, smoke: 0, leaves: 0 };
      const motes = world?.ship?.motes;
      const s = world?.ship ?? null;
      const shipStats: ShipStats | null =
        s === null || world === null
          ? null
          : {
              preset: world.blend.current.name,
              flight: s.motion?.blend() ?? flight,
              reveals: Object.fromEntries(s.reveals.map((r) => [r.room, r.fade.value])),
              hatch: [...s.hatches.values()].map((h) => h.phase()).find((ph) => ph !== 'idle') ?? 'idle',
              crew: model === null ? 0 : model.residents.filter((r) => r.present && r.post !== null).length,
              clutter: s.clutter?.mesh.visible === true ? (current?.scene.placements.filter((p) => p.prefab === 'ship_clutter').length ?? 0) : 0,
              sky: s.sky?.mesh.visible === true,
              glare: medium() ? lastGlare : 0,
            };
      const scene: SceneStats = {
        id: world?.scene.id ?? null,
        particles: { ...counts, motes: motes !== undefined && motes !== null && motes.points.visible ? motes.count : 0 },
        lights: world?.lights.counts() ?? { point: 0, spot: 0, shadowed: 0 },
        birds: world?.town !== null && world?.town !== undefined && world.town.birds.mesh.visible ? world.town.birds.count : 0,
        revision: drawnRevision,
        batchedMeshes: world?.merged ?? 0,
        fov: camera.fov,
        framing: world?.camera.framing ?? null,
        ship: shipStats,
      };
      const t = world?.camera.target ?? new THREE.Vector3();
      const p = feet.get(travelerId);
      return {
        ...last,
        tier,
        pixelRatio: renderer.getPixelRatio(),
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        cpuFrameSamples: [...cpuSamples],
        animationFrameSamples: [...intervals],
        gpuMs: gpu.last(),
        gpuSamples: gpu.samples(),
        gpuMeasured: gpu.measured(),
        buildMs,
        buildFrame,
        geometries: renderer.info.memory.geometries,
        textures: renderer.info.memory.textures,
        programs: renderer.info.programs?.length ?? 0,
        camera: { target: [t.x, t.y, t.z], leader: p === undefined ? null : [p.x, p.y], lead: world?.camera.lead() ?? 0 },
        contextLosses: losses,
        contextRestores: restores,
        peak: { ...peak },
        lastFrame: { shadowRedraw: false },
        itemsShown: 0,
        sprites: { loaded: installed.size, ...units.spriteCounts(everyone) },
        scene,
      };
    },
    resetPeak() {
      peak = { drawCalls: 0, triangles: 0 };
    },
    async loadSprites(sources) {
      const waits: Promise<void>[] = [];
      for (const source of sources) {
        if (requested.has(source.id)) continue;
        requested.add(source.id);
        const ready = preparedSheet(source);
        if (ready !== undefined) {
          land(ready);
          continue;
        }
        waits.push(
          loadSheet(source).then(land, (e: unknown) => {
            requested.delete(source.id);
            console.warn(`sprite ${source.id} not installed (it draws as its proxy): ${e instanceof Error ? e.message : String(e)}`);
          }),
        );
      }
      await Promise.all(waits);
      return [...installed];
    },
    spriteState: (id) => units.spriteState(id),
    artwork: () => world?.town?.kit.artwork() ?? {},
    material(name) {
      let found: THREE.MeshStandardMaterial | null = null;
      world?.root.traverse((o) => {
        if (found !== null || !(o instanceof THREE.Mesh)) return;
        const list: THREE.Material[] = Array.isArray(o.material) ? (o.material as THREE.Material[]) : [o.material as THREE.Material];
        for (const mm of list) if (found === null && mm.name === name && mm instanceof THREE.MeshStandardMaterial) found = mm;
      });
      if (found === null) return null;
      const f: THREE.MeshStandardMaterial = found;
      const image: unknown = f.map?.image;
      return { hooks: hookKeys(f), color: `#${f.color.getHexString()}`, bumpScale: f.bumpScale, map: image instanceof HTMLImageElement ? image.src : image === undefined || image === null ? null : 'procedural' };
    },
    probeOcclusion(x, y, ao) {
      if (world === null || model === null || !medium() || lost) return null;
      const w = world;
      const buffer = renderer.getDrawingBufferSize(new THREE.Vector2());
      const u = x / Math.max(1, canvas.clientWidth);
      const v = 1 - y / Math.max(1, canvas.clientHeight);
      const px = Math.min(buffer.x - 1, Math.floor(u * buffer.x));
      const py = Math.min(buffer.y - 1, Math.floor(v * buffer.y));
      const gl = renderer.getContext();
      const screen = (): number => {
        gl.readPixels(px, py, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, syncPixel);
        return (0.2126 * (syncPixel[0] ?? 0) + 0.7152 * (syncPixel[1] ?? 0) + 0.0722 * (syncPixel[2] ?? 0)) / 255;
      };
      w.lens.forceOcclusion(1);
      render(0);
      const open = screen();
      w.lens.forceOcclusion(ao);
      render(0);
      const shut = screen();
      const probe = w.lens.probeOcclusion(renderer, u, v);
      w.lens.forceOcclusion(null);
      render(0);
      return probe === null ? null : { ratio: probe.ratio, beauty: probe.beauty, screen: [open, shut], lensInput: probe.lensInput };
    },
    flocks: () => world?.town?.birds.flocks() ?? [],
    setAb(flags) {
      ab = { ...ab, ...flags };
      if (world !== null && !lost) applyTier();
      wake();
    },
    snapCamera() {
      snapCamera = true;
      dirty = true;
    },
    setFraming(name) {
      framing = name;
      world?.camera.setFraming(name);
      snapCamera = true;
      dirty = true;
      wake();
    },
    setFlight(flying) {
      flight = flying ? 1 : 0;
      dirty = true;
      wake();
    },
    setEggs(states) {
      eggs = states;
      if (world !== null) applyState(world);
      if (medium()) renderer.shadowMap.needsUpdate = true;
      dirty = true;
      wake();
    },
    playHatch(spot) {
      const d = world?.ship?.hatches.get(spot);
      endHatch();
      if (d === undefined || lost || disposed) return Promise.resolve();
      d.start(eventTime);
      dirty = true;
      wake();
      return new Promise<void>((resolve) => {
        hatchPlaying = { started: eventTime, seconds: d.duration(), done: resolve };
      });
    },
    setGuests(next) {
      for (const g of guests) if (!next.some((n) => n.id === g.id)) units.remove(g.id);
      guests = next;
      dirty = true;
      wake();
    },
    dispose() {
      if (disposed) return { geometries: 0, textures: 0 };
      disposed = true;
      endHatch();
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      world?.dispose();
      world = null;
      freeAtlases();
      units.dispose();
      hint.dispose();
      gpu.dispose();
      for (const x of [proxyMaterial, box, disc, blobMaterial]) x.dispose();
      const left = { geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures };
      // The next view's renderer starts on this context assuming three's default unpack state: put the context's back
      // (a canvas texture leaves FLIP_Y on, which WebGL refuses for the next view's first 3D texture upload).
      const gl = renderer.getContext();
      if (!gl.isContextLost()) {
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      }
      renderer.dispose();
      return left;
    },
  };
};
